import React, { useState, useEffect } from 'react';
import SEO from './SEO';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowsUpDownIcon, FunnelIcon, LockClosedIcon, InformationCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/20/solid';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import DisclaimerFooter from './DisclaimerFooter';
import { signalDescriptions, rankDescriptions } from '../constants/signalDescriptions';
import { generateDataTablePDF, formatPDFDate, formatPDFNumber } from '../utils/pdfExport';

export default function FullPowerListPage() {
  const [rankingsData, setRankingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [pdfExportLoading, setPdfExportLoading] = useState(false);
  const navigate = useNavigate();
  
  // Simplified filtering state - only sector
  const [filters, setFilters] = useState({
    sector: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'rank_global',
    direction: 'asc'
  });
  const [activeTooltip, setActiveTooltip] = useState(null);
  
  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Helper function to format score values
  const formatScore = (value, isMasked = false) => {
    if (isMasked) {
      return (
        <span className="inline-flex items-center rounded-full bg-bg-300/50 px-2 py-1 text-xs">
          <LockClosedIcon className="h-3 w-3 mr-1" />
        </span>
      );
    }
    
    if (value === null || value === undefined || value === '') {
      return <span className="text-text-300">–</span>;
    }
    
    return Math.round(parseFloat(value)).toString();
  };

  // Toggle tooltip function
  const toggleTooltip = (factor) => {
    if (activeTooltip === factor) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(factor);
    }
  };

  useEffect(() => {
    const fetchRankings = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        
        // Build query parameters for filters
        const params = new URLSearchParams();
        params.append('type', 'stock_rankings');
        
        if (filters.sector) params.append('sector', filters.sector);
        
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/ea_stock_rankings?${params.toString()}`,
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
            },
          }
        );
        setRankingsData(response.data);
      } catch (error) {
        console.error('Error fetching stock rankings:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRankings();
  }, [navigate, filters]);

  const handleSecurityClick = (security) => {
    // Only navigate if the security is not masked
    if (!security.is_masked) {
      navigate(`/ticker/${security.security_id}`, { 
        state: { security }
      });
    }
  };

  // Sorting function
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted data
  const getSortedData = () => {
    if (!rankingsData?.rankings) return [];
    
    const sortableItems = [...rankingsData.rankings];
    
    sortableItems.sort((a, b) => {
      let aValue, bValue;
      
      // Handle nested component scores
      if (sortConfig.key.includes('.')) {
        const [parent, child] = sortConfig.key.split('.');
        aValue = a[parent]?.[child] || 0;
        bValue = b[parent]?.[child] || 0;
      } else {
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
      }
      
      // Handle string sorting for ticker and company_name
      if (sortConfig.key === 'ticker' || sortConfig.key === 'company_name') {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }
      
      // Handle numeric sorting - Primary sort
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      
      // If values are equal, use rank_global as a secondary sort
      return a.rank_global - b.rank_global;
    });
    
    return sortableItems;
  };

  // Filter panel component - simplified to only show sector
  const FilterPanel = () => {
    if (!showFilters) return null;
    
    return (
      <div className="bg-bg-200 rounded-lg p-3 mb-6 mt-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 max-w-xs">
            <label className="block text-xs font-medium text-text-100 mb-1">Filter by Sector</label>
            <select
              value={filters.sector}
              onChange={(e) => handleFilterChange('sector', e.target.value)}
              className="bg-bg-300 text-text-100 text-sm rounded-lg focus:ring-primary-100 focus:border-primary-100 block w-full p-2"
            >
              <option value="">All Sectors</option>
              {rankingsData?.available_filters?.sectors?.map((sector) => (
                <option key={sector.sector_id} value={sector.sector_name}>
                  {sector.sector_name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex-shrink-0">
            <button
              onClick={() => {
                setFilters({ sector: '' });
              }}
              className="text-text-200 bg-bg-300 hover:bg-bg-400 hover:text-text-100 px-2 py-1.5 rounded-md text-xs"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Subscription banner for unapproved users
  const SubscriptionBanner = () => {
    if (rankingsData?.is_approved) return null;
    
    return (
      <div className="mt-4 mb-6 bg-gradient-to-r from-primary-100/20 to-accent-100/20 rounded-lg p-6">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="mb-4 md:mb-0 md:mr-6">
            <h3 className="text-lg font-semibold text-text-100">
              <LockClosedIcon className="h-5 w-5 inline-block mr-2" />
              Unlock Full Access to All Stock Rankings
            </h3>
            <p className="mt-1 text-text-200">
              Get detailed component scores, full rankings, and personalized alerts for all securities.
            </p>
          </div>
          <div>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center rounded-md bg-primary-100 px-4 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
            >
              Request Full Access
            </button>
          </div>
        </div>
      </div>
    );
  };

  // CSV Export function
  const exportToCSV = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Build query parameters for filters (same as current filters)
      const params = new URLSearchParams();
      
      if (filters.sector) params.append('sector', filters.sector);
      
      // Use the dedicated export endpoint
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/ea_stock_rankings/export?${params.toString()}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Export failed');
      }
      
      const allData = response.data.data;
      
      // Convert to CSV
      const csvContent = convertToCSV(allData, response.data.is_approved);
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Include filter info in filename
      let filename = `stock_rankings_${new Date().toISOString().split('T')[0]}`;
      if (filters.sector) filename += `_${filters.sector.replace(/\s+/g, '_')}`;
      filename += '.csv';
      
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      if (error.response?.status === 401) {
        alert('Please log in to export data.');
        navigate('/login');
      } else {
        alert('Failed to export CSV. Please try again.');
      }
    } finally {
      setExportLoading(false);
    }
  };

  // PDF Export function
  const exportToPDF = async () => {
    setPdfExportLoading(true);
    try {
      const sortedData = getSortedData();
      
      if (!sortedData || sortedData.length === 0) {
        alert('No data to export');
        return;
      }

      // Prepare data for PDF - use existing data structure from API
      const pdfData = sortedData.map(stock => [
        stock.rank_global || 'N/A',
        stock.ticker || 'N/A',
        stock.name || 'N/A',
        stock.sector || 'N/A',
        (stock.signal_weight !== null && stock.signal_weight !== undefined) ? formatPDFNumber(stock.signal_weight, 1) : 'N/A',
        (stock.component_scores?.value !== null && stock.component_scores?.value !== undefined) ? formatPDFNumber(stock.component_scores.value, 0) : 'N/A',
        (stock.component_scores?.momentum !== null && stock.component_scores?.momentum !== undefined) ? formatPDFNumber(stock.component_scores.momentum, 0) : 'N/A',
        (stock.component_scores?.quality !== null && stock.component_scores?.quality !== undefined) ? formatPDFNumber(stock.component_scores.quality, 0) : 'N/A',
        (stock.component_scores?.lowvol !== null && stock.component_scores?.lowvol !== undefined) ? formatPDFNumber(stock.component_scores.lowvol, 0) : 'N/A',
        (stock.component_scores?.earnings !== null && stock.component_scores?.earnings !== undefined) ? formatPDFNumber(stock.component_scores.earnings, 0) : 'N/A',
        (stock.component_scores?.transcripts !== null && stock.component_scores?.transcripts !== undefined) ? formatPDFNumber(stock.component_scores.transcripts, 0) : 'N/A',
        (stock.component_scores?.innovation !== null && stock.component_scores?.innovation !== undefined) ? formatPDFNumber(stock.component_scores.innovation, 0) : 'N/A'
      ]);

      // Active filters
      const activeFilters = {};
      if (filters.sector) {
        activeFilters['Sector'] = filters.sector;
      }

      await generateDataTablePDF({
        title: 'Power List Rankings',
        subtitle: `SharpeMetrix Stock Rankings (showing ${sortedData.length} stocks)`,
        columns: ['Rank', 'Ticker', 'Company', 'Sector', 'Signal', 'Val', 'Mom', 'Qual', 'LowVol', 'Earn', 'Trans', 'Innov'],
        data: pdfData,
        filename: `SharpeMetrix_PowerList_${rankingsData?.as_of_date || new Date().toISOString().split('T')[0]}.pdf`,
        filters: activeFilters,
        dateLabel: 'As of Date',
        date: rankingsData?.as_of_date ? formatPDFDate(rankingsData.as_of_date) : formatPDFDate(new Date()),
        tableStyles: {
          fontSize: 8,
          cellPadding: 2,
          halign: 'center',
          cellWidth: 'auto'
        }
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setPdfExportLoading(false);
    }
  };

  // Convert data to CSV format
  const convertToCSV = (data, isApproved) => {
    if (!data || data.length === 0) return '';
    
    // Define headers
    const headers = [
                              'Rank',
      'Ticker',
      'Company Name',
      'Sector',
      'Price',
      'Market Cap (M)',
      'Signal Score',
      'Value Score',
      'Momentum Score',
      'Quality Score',
      'Low Vol Score',
      'Earnings Score',
      'Transcripts Score',
      'Innovation Score',
      'Security ID',
      'As Of Date'
    ];
    
    // Convert data to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...data.map(stock => {
        const row = [
          stock.global_rank,
          `"${stock.ticker}"`,
          `"${stock.company_name.replace(/"/g, '""')}"`, // Escape quotes
          `"${stock.sector || ''}"`,
          stock.price || '',
          stock.market_cap_millions || '',
          stock.signal_score || '',
          stock.value_score || '',
          stock.momentum_score || '',
          stock.quality_score || '',
          stock.lowvol_score || '',
          stock.earnings_score || '',
          stock.transcripts_score || '',
          stock.innovation_score || '',
          stock.security_id,
          stock.as_of_date
        ];
        return row.join(',');
      })
    ];
    
    return csvRows.join('\n');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-100 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-100"></div>
      </div>
    );
  }

  const sortedData = getSortedData();

  return (
    <div className="flex flex-col min-h-screen">
      <SEO title="Power List™" description="Proprietary stock rankings scored with alternative data signals before the crowd reacts." />
      <div className="flex-grow bg-bg-100 px-4 sm:px-6 lg:px-8 py-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-text-100">Power List</h1>
            <p className="mt-2 text-sm text-text-200">
              Comprehensive stock analysis and rankings based on our proprietary algorithms.
            </p>
            <div className="mt-4 relative">
              <div className="bg-gradient-to-r from-blue-600/20 via-blue-500/15 to-cyan-500/20 rounded-lg border border-blue-400/30 p-4 backdrop-blur-sm">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <InformationCircleIcon className="h-5 w-5 text-blue-300" />
                  </div>
                  <p className="text-sm text-white leading-relaxed">
                    Our proprietary ranking methodology combines fundamental analysis, market sentiment, and alternative data to identify the most promising investment opportunities. Updated monthly, this list represents our highest-conviction stock selections based on our systematic approach to market analysis.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={exportToCSV}
                disabled={exportLoading}
                className="inline-flex items-center rounded-md border border-white bg-transparent px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white hover:text-bg-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-text-100 mr-1.5"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Export CSV
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={exportToPDF}
                disabled={pdfExportLoading}
                className="inline-flex items-center rounded-md border border-white bg-transparent px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white hover:text-bg-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pdfExportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-text-100 mr-1.5"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <DocumentArrowDownIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Export PDF
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center rounded-md bg-primary-100 px-2 py-1.5 text-xs font-medium text-text-100 shadow-sm hover:bg-primary-200"
              >
                <FunnelIcon className="-ml-0.5 mr-1 h-4 w-4" aria-hidden="true" />
                {showFilters ? 'Hide' : 'Filter'}
              </button>
            </div>
          </div>
        </div>

        <SubscriptionBanner />
        <FilterPanel />

        <div className="mt-4 flow-root">
          <div className="bg-bg-200 rounded-lg shadow-xl overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              {rankingsData?.as_of_date && (
                <div className="mb-4 text-sm text-text-200">
                  Data as of: {rankingsData.as_of_date}
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-bg-300 table-fixed">
                  <thead>
                    <tr>
                      <th 
                        onClick={() => requestSort('rank_global')}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                      >
                        <div className="flex items-center">
                          Rank
                          {sortConfig.key === 'rank_global' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('ticker')}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                      >
                        <div className="flex items-center">
                          Ticker
                          {sortConfig.key === 'ticker' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('company_name')}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300" 
                        style={{width: '200px', maxWidth: '200px'}}
                      >
                        <div className="flex items-center">
                          Company Name
                          {sortConfig.key === 'company_name' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('rank_change')}
                        className="px-3 py-3.5 text-center text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                      >
                        <div className="flex items-center justify-center">
                          Rank Change
                          <div className="relative">
                            <button
                              type="button"
                              className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTooltip('rankChange');
                              }}
                              onMouseEnter={() => setActiveTooltip('rankChange')}
                              onMouseLeave={() => setActiveTooltip(null)}
                              aria-label="Information about Rank Change"
                            >
                              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                            {activeTooltip === 'rankChange' && (
                              <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm font-normal text-left text-text-100 w-80 left-1/2 -translate-x-1/2 top-full mt-2 border border-bg-400">
                                {rankDescriptions.rankChange}
                              </div>
                            )}
                          </div>
                          {sortConfig.key === 'rank_change' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('sector')}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                      >
                        <div className="flex items-center">
                          Sector
                          {sortConfig.key === 'sector' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('price')}
                        className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                      >
                        <div className="flex items-center justify-end">
                          Price
                          {sortConfig.key === 'price' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
       
                      <th 
                        onClick={() => requestSort('signal_weight')}
                        className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                      >
                        <div className="flex items-center justify-end">
                          Signal Score
                          {sortConfig.key === 'signal_weight' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      {/* Component Score Headers - Always visible */}
                      <th 
                        onClick={() => requestSort('component_scores.value')}
                        className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                      >
                        <div className="flex items-center justify-end">
                          Value
                          <div className="relative">
                            <button
                              type="button"
                              className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTooltip('value');
                              }}
                              onMouseEnter={() => setActiveTooltip('value')}
                              onMouseLeave={() => setActiveTooltip(null)}
                              aria-label="Information about Value"
                            >
                              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                            {activeTooltip === 'value' && (
                              <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                {signalDescriptions.value}
                              </div>
                            )}
                          </div>
                          {sortConfig.key === 'component_scores.value' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('component_scores.momentum')}
                        className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                      >
                        <div className="flex items-center justify-end">
                          Momentum
                          <div className="relative">
                            <button
                              type="button"
                              className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTooltip('momentum');
                              }}
                              onMouseEnter={() => setActiveTooltip('momentum')}
                              onMouseLeave={() => setActiveTooltip(null)}
                              aria-label="Information about Momentum"
                            >
                              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                            {activeTooltip === 'momentum' && (
                              <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                {signalDescriptions.momentum}
                              </div>
                            )}
                          </div>
                          {sortConfig.key === 'component_scores.momentum' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('component_scores.quality')}
                        className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                      >
                        <div className="flex items-center justify-end">
                          Quality
                          <div className="relative">
                            <button
                              type="button"
                              className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTooltip('quality');
                              }}
                              onMouseEnter={() => setActiveTooltip('quality')}
                              onMouseLeave={() => setActiveTooltip(null)}
                              aria-label="Information about Quality"
                            >
                              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                            {activeTooltip === 'quality' && (
                              <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                {signalDescriptions.quality}
                              </div>
                            )}
                          </div>
                          {sortConfig.key === 'component_scores.quality' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('component_scores.lowvol')}
                        className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                      >
                        <div className="flex items-center justify-end">
                          Low Vol
                          <div className="relative">
                            <button
                              type="button"
                              className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTooltip('lowvol');
                              }}
                              onMouseEnter={() => setActiveTooltip('lowvol')}
                              onMouseLeave={() => setActiveTooltip(null)}
                              aria-label="Information about Low Vol"
                            >
                              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                            {activeTooltip === 'lowvol' && (
                              <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                {signalDescriptions.lowvol}
                              </div>
                            )}
                          </div>
                          {sortConfig.key === 'component_scores.lowvol' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('component_scores.earnings')}
                        className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                      >
                        <div className="flex items-center justify-end">
                          Earnings
                          <div className="relative">
                            <button
                              type="button"
                              className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTooltip('earnings');
                              }}
                              onMouseEnter={() => setActiveTooltip('earnings')}
                              onMouseLeave={() => setActiveTooltip(null)}
                              aria-label="Information about Earnings"
                            >
                              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                            {activeTooltip === 'earnings' && (
                              <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                {signalDescriptions.earnings}
                              </div>
                            )}
                          </div>
                          {sortConfig.key === 'component_scores.earnings' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('component_scores.transcripts')}
                        className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                      >
                        <div className="flex items-center justify-end">
                          Transcripts
                          <div className="relative">
                            <button
                              type="button"
                              className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTooltip('transcripts');
                              }}
                              onMouseEnter={() => setActiveTooltip('transcripts')}
                              onMouseLeave={() => setActiveTooltip(null)}
                              aria-label="Information about Transcripts"
                            >
                              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                            {activeTooltip === 'transcripts' && (
                              <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                {signalDescriptions.transcripts}
                              </div>
                            )}
                          </div>
                          {sortConfig.key === 'component_scores.transcripts' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => requestSort('component_scores.innovation')}
                        className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                      >
                        <div className="flex items-center justify-end">
                          Innovation
                          <div className="relative">
                            <button
                              type="button"
                              className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTooltip('innovation');
                              }}
                              onMouseEnter={() => setActiveTooltip('innovation')}
                              onMouseLeave={() => setActiveTooltip(null)}
                              aria-label="Information about Innovation"
                            >
                              <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                            {activeTooltip === 'innovation' && (
                              <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                {signalDescriptions.innovation}
                              </div>
                            )}
                          </div>
                          {sortConfig.key === 'component_scores.innovation' && (
                            <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-bg-300">
                    {sortedData.map((stock) => (
                      <tr 
                        key={stock.security_id}
                        onClick={() => !stock.is_masked && handleSecurityClick(stock)}
                        className={`${!stock.is_masked ? 'hover:bg-bg-300/50 cursor-pointer' : ''} ${stock.is_masked ? 'bg-bg-300/30' : ''}`}
                      >
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                          {stock.rank_global}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                          {stock.ticker}
                          {stock.is_masked && <LockClosedIcon className="h-3 w-3 inline-block ml-1" />}
                        </td>
                        <td className="px-3 py-4 text-sm text-text-200" style={{width: '200px', maxWidth: '200px'}}>
                          <div className="truncate overflow-hidden whitespace-nowrap" title={stock.name}>
                            {stock.name}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                          {stock.rank_change !== null && stock.rank_change !== undefined ? (
                            <div className={`inline-flex items-center ${
                              stock.rank_change > 0 ? 'text-green-400' : 
                              stock.rank_change < 0 ? 'text-red-400' : 
                              'text-text-200'
                            }`}>
                              {stock.rank_change > 0 && (
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414 6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              {stock.rank_change < 0 && (
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              {stock.rank_change === 0 ? '—' : Math.abs(stock.rank_change)}
                            </div>
                          ) : (
                            <span className="text-text-300">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                          {stock.sector}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                          {stock.price ? (
                            <div className="flex justify-end">
                              <span className="mr-1">$</span>
                              <span>{parseFloat(stock.price).toFixed(2)}</span>
                            </div>
                          ) : '–'}
                        </td>
              
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                          {stock.is_masked ? (
                            <span className="inline-flex items-center rounded-full bg-bg-300 px-2 py-1 text-xs">
                              <LockClosedIcon className="h-3 w-3 mr-1" /> 
                              Subscribe
                            </span>
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              stock.signal_weight >= 80 ? 'bg-green-400/10 text-green-400' :
                              stock.signal_weight >= 60 ? 'bg-green-300/10 text-green-300' :
                              stock.signal_weight >= 40 ? 'bg-yellow-300/10 text-yellow-300' :
                              stock.signal_weight >= 20 ? 'bg-red-300/10 text-red-300' :
                              'bg-red-400/10 text-red-400'
                            }`}>
                              {formatScore(stock.signal_weight)}
                            </span>
                          )}
                        </td>
                        
                        {/* Component Scores - Always visible */}
                        <>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                            {formatScore(stock.component_scores.value, stock.is_masked)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                            {formatScore(stock.component_scores.momentum, stock.is_masked)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                            {formatScore(stock.component_scores.quality, stock.is_masked)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                            {formatScore(stock.component_scores.lowvol, stock.is_masked)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                            {formatScore(stock.component_scores.earnings, stock.is_masked)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                            {formatScore(stock.component_scores.transcripts, stock.is_masked)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                            {formatScore(stock.component_scores.innovation, stock.is_masked)}
                          </td>
                        </>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Enterprise CTA Section */}
        {!rankingsData?.is_approved && (
          <div className="mt-8">
            <div className="rounded-lg bg-gradient-to-r from-primary-100/10 to-accent-100/10 p-8">
              <div className="mx-auto max-w-md text-center">
                <h2 className="text-xl font-semibold text-text-100">
                  Want Access to Our Complete Rankings?
                </h2>
                <p className="mt-2 text-text-200">
                  Get access to our comprehensive rankings for over 25,000 securities, including detailed analytics, component score breakdowns, 
                  real-time updates, and custom screening tools.
                </p>
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/contact')}
                    className="inline-flex items-center rounded-md bg-primary-100 px-3.5 py-2.5 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
                  >
                    Contact Us for Enterprise Access
                    <span className="ml-2" aria-hidden="true">&rarr;</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
} 